import { ref, watch, onMounted, onUnmounted } from "vue";
const CODE_MIN_DIGITS = 8;
const SCAN_MIN_KEYS = 4;
const SCAN_MIN_KEYS_BEFORE_ENTER = 2;
const SCAN_MAX_GAP_MS = 30;
const RUN_PAUSE_MS = 500;
function createCodeDetector() {
  let run = "";
  let lastAt = 0;
  let streakFrom = 0;
  const streak = () => run.length - streakFrom;
  const codeFrom = () => streak() >= 2 ? streakFrom : 0;
  return {
    /** A character typed while Nova cena has the cursor. `started`: it begins a new run;
     *  `code`: the code, once the run holds one. */
    push(key, now, repeat = false) {
      const started = !run || now - lastAt >= RUN_PAUSE_MS;
      if (started) {
        run = "";
        streakFrom = 0;
      } else if (now - lastAt >= SCAN_MAX_GAP_MS) {
        streakFrom = run.length;
      }
      run += key;
      lastAt = now;
      if (repeat) streakFrom = run.length;
      const scanned = run.slice(streakFrom);
      const isCode = /^[0-9]+$/.test(run) && run.length >= CODE_MIN_DIGITS || scanned.length >= SCAN_MIN_KEYS || scanned.length >= SCAN_MIN_KEYS_BEFORE_ENTER && /[^0-9.,]/.test(scanned);
      if (!isCode) return { started, code: null };
      const from = codeFrom();
      return { started, code: { code: run.slice(from), from } };
    },
    /** Enter: the short code it ends when it comes at a scanner's pace, else null. */
    enter(now) {
      if (streak() < SCAN_MIN_KEYS_BEFORE_ENTER || now - lastAt >= SCAN_MAX_GAP_MS) return null;
      return { code: run.slice(streakFrom), from: streakFrom };
    },
    /** Backspace takes the last character back (a person's key: no scanner sends it). */
    backspace() {
      run = run.slice(0, -1);
      streakFrom = run.length;
    },
    reset() {
      run = "";
      streakFrom = 0;
    }
  };
}
function usePriceModeKeys(host) {
  const { editor } = host;
  const detector = createCodeDetector();
  let before = [];
  const searchActive = ref(false);
  function resetRun() {
    detector.reset();
    before = [];
  }
  watch(() => editor.fresh.value, (fresh) => {
    if (fresh) resetRun();
  });
  function toPrice() {
    const input = host.searchInput();
    if (input && document.activeElement === input) input.blur();
  }
  function toSearch(clear) {
    var _a;
    if (clear) {
      editor.setQuery("");
      editor.setChangesOnly(false);
      editor.clearSelection();
    }
    (_a = host.searchInput()) == null ? void 0 : _a.focus();
  }
  function pick() {
    const result = editor.pickFirst();
    if (result === "selected") {
      toPrice();
      host.reveal();
    } else if (result === "none" || result === "locked") {
      editor.setQuery("");
      host.notFound(result);
    }
  }
  function next() {
    resetRun();
    const result = editor.move(1);
    if (result === "moved") host.reveal();
    else if (result === "end") toSearch(true);
  }
  function searchCode(code) {
    editor.setQuery(code);
    pick();
  }
  function takeBack(code, complete) {
    var _a;
    const previous = before[code.from];
    if (previous) {
      editor.setEntry(previous.entry);
      editor.fresh.value = previous.fresh;
    }
    resetRun();
    if (complete) {
      searchCode(code.code);
      return;
    }
    editor.setQuery(code.code);
    (_a = host.searchInput()) == null ? void 0 : _a.focus();
  }
  function ownsKeys(target) {
    if (!target || target === document.body || target === document.documentElement) return true;
    return !!target.closest(host.ownSelector);
  }
  function onSearchKey(event) {
    if (event.key === "Enter" || event.key === "ArrowDown") {
      event.preventDefault();
      pick();
    } else if (event.key === "Escape" && editor.query.value) {
      event.preventDefault();
      editor.setQuery("");
    }
  }
  function onPriceKey(event) {
    const key = event.key;
    if (key === "Enter") {
      event.preventDefault();
      const code2 = detector.enter(event.timeStamp);
      if (code2) {
        takeBack(code2, true);
        return;
      }
      next();
      return;
    }
    if (key === "ArrowDown" || key === "ArrowUp") {
      event.preventDefault();
      resetRun();
      if (editor.move(key === "ArrowUp" ? -1 : 1) === "moved") host.reveal();
      return;
    }
    if (key === "Escape") {
      resetRun();
      if (!editor.revert()) toSearch(true);
      return;
    }
    if (key === "Backspace") {
      event.preventDefault();
      detector.backspace();
      before.pop();
      if (editor.selected.value) host.typeKey(event);
      return;
    }
    if (key.length !== 1 || /\s/.test(key)) return;
    event.preventDefault();
    const priceKey = /^[0-9.,]$/.test(key);
    if (priceKey && !editor.selected.value && !editor.selectFirstEditable()) return;
    const previous = { entry: editor.entry.value, fresh: editor.fresh.value };
    const { started, code } = detector.push(key, event.timeStamp, event.repeat);
    if (started) before = [];
    before.push(previous);
    if (code) {
      takeBack(code, false);
      return;
    }
    if (priceKey) host.typeKey(event);
  }
  function onKeydown(event) {
    if (event.defaultPrevented || event.metaKey || event.ctrlKey || event.altKey) return;
    if (document.querySelector('[role="dialog"], [role="alertdialog"]')) return;
    const target = event.target;
    if (target && target === host.searchInput()) {
      onSearchKey(event);
      return;
    }
    if (ownsKeys(target)) onPriceKey(event);
  }
  function onFocusChange() {
    const input = host.searchInput();
    searchActive.value = !!input && document.activeElement === input;
  }
  onMounted(() => {
    document.addEventListener("keydown", onKeydown);
    document.addEventListener("focusin", onFocusChange);
    document.addEventListener("focusout", onFocusChange);
  });
  onUnmounted(() => {
    document.removeEventListener("keydown", onKeydown);
    document.removeEventListener("focusin", onFocusChange);
    document.removeEventListener("focusout", onFocusChange);
  });
  return { searchActive, toSearch, toPrice, next, searchCode };
}
export {
  CODE_MIN_DIGITS,
  createCodeDetector,
  usePriceModeKeys
};
//# sourceMappingURL=index55.js.map
